import { useCallback, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useTranslation } from 'react-i18next';

import {
  DRY_RUN_UNAVAILABLE_MESSAGE,
  useDryRunExaminerGate,
} from '@/common/hooks/oralExam/useDryRunExaminerGate';
import { getOralExamApiErrorMessage } from '@/common/hooks/oralExam/oralExamApiError';
import { useEnsureOralExamPractice } from '@/common/hooks/oralExam/useOralExamPractice';
import type { StudentRouteParamList } from '@/common/navigation/RoleNavigations/Student/types';
import {
  fetchOralExamPracticeAvailability,
  type OralExamPracticeEnsure,
} from '@/common/utils/oralExam/practiceApi';

export type EnterOralExamPracticeLobbyResult = 'entered' | 'unavailable';

type EnterOralExamPracticeLobbyParams = {
  taskId: string;
  mutateAsync: () => Promise<OralExamPracticeEnsure>;
  confirmBeforeStart: () => Promise<boolean>;
  navigate: NativeStackNavigationProp<StudentRouteParamList>['navigate'];
};

/** Task-page dry-run entry: availability → canary → ensure → meeting route. */
export async function enterOralExamPracticeLobby({
  taskId,
  mutateAsync,
  confirmBeforeStart,
  navigate,
}: EnterOralExamPracticeLobbyParams): Promise<EnterOralExamPracticeLobbyResult> {
  const availability = await fetchOralExamPracticeAvailability(taskId);
  if (!availability.available) {
    return 'unavailable';
  }
  const canaryOk = await confirmBeforeStart();
  if (!canaryOk) {
    return 'unavailable';
  }
  const ensured = await mutateAsync();
  navigate('SlotMeetingScreen', {
    courseId: ensured.courseId,
    moduleId: ensured.moduleId,
    taskId: ensured.taskId,
    sessionId: ensured.sessionId,
    bookingId: ensured.bookingId ?? ensured.sessionId,
  });
  return 'entered';
}

export function useEnterOralExamPracticeLobby(taskId: string | undefined) {
  const { t } = useTranslation();
  const navigation = useNavigation<NativeStackNavigationProp<StudentRouteParamList>>();
  const ensure = useEnsureOralExamPractice(taskId);
  const { confirmBeforeStart } = useDryRunExaminerGate(Boolean(taskId));
  const [confirmingCanary, setConfirmingCanary] = useState(false);
  const [enterError, setEnterError] = useState<string | null>(null);
  const [unavailableAfterLastAttempt, setUnavailableAfterLastAttempt] = useState(false);

  const enterPracticeLobby = useCallback(async () => {
    if (!taskId || confirmingCanary || ensure.isPending) {
      return;
    }
    setEnterError(null);
    setUnavailableAfterLastAttempt(false);
    setConfirmingCanary(true);
    try {
      const result = await enterOralExamPracticeLobby({
        taskId,
        mutateAsync: () => ensure.mutateAsync(),
        confirmBeforeStart,
        navigate: navigation.navigate,
      });
      if (result === 'unavailable') {
        setUnavailableAfterLastAttempt(true);
        setEnterError(t('oralExam.practice.unavailable', {
          defaultValue: DRY_RUN_UNAVAILABLE_MESSAGE,
        }));
      }
    } catch (error) {
      setEnterError(getOralExamApiErrorMessage(
        error,
        t('oralExam.practice.startFailed', {
          defaultValue: 'Could not start the dry run. Try again.',
        }),
      ));
    } finally {
      setConfirmingCanary(false);
    }
  }, [confirmBeforeStart, confirmingCanary, ensure, navigation.navigate, t, taskId]);

  return {
    enterPracticeLobby,
    isEnteringPractice: confirmingCanary || ensure.isPending,
    enterPracticeError: enterError,
    unavailableAfterLastAttempt,
  };
}
